import React, { useRef, useEffect, useState, useCallback } from 'react';

interface DualRangeSliderProps {
  min: number;
  max: number;
  step?: number;
  value: { min: number; max: number };
  onChange: (value: { min: number; max: number }) => void;
  className?: string;
  themeColor?: string;
  unit?: string;
}

export const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min,
  max,
  step = 1,
  value,
  onChange,
  className = '',
  themeColor = '#3b82f6',
}) => {
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Safe helper to keep value within bounds
  const clamp = (val: number, minVal: number, maxVal: number) => {
    return Math.min(Math.max(val, minVal), maxVal);
  };

  // Convert value to percentage for positioning
  const getPercent = useCallback(
    (val: number) => Math.round(((val - min) / (max - min)) * 100),
    [min, max],
  );

  // Handle move logic
  const handleMove = useCallback(
    (clientX: number) => {
      if (!isDragging || !trackRef.current) return;

      const rect = trackRef.current.getBoundingClientRect();
      const percent = clamp((clientX - rect.left) / rect.width, 0, 1);
      const rawValue = min + percent * (max - min);

      // Snap to step
      const steppedValue = Math.round(rawValue / step) * step;

      // Precision fix for floating point math
      const newValue = Number(steppedValue.toFixed(1));

      if (isDragging === 'min') {
        const newMin = Math.min(newValue, value.max - step);
        onChange({ ...value, min: clamp(newMin, min, max - step) });
      } else {
        const newMax = Math.max(newValue, value.min + step);
        onChange({ ...value, max: clamp(newMax, min + step, max) });
      }
    },
    [isDragging, min, max, step, value, onChange],
  );

  // Mouse/Touch Event Listeners
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const onTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX);

    const onEnd = () => setIsDragging(null);

    if (isDragging) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onEnd);
      document.addEventListener('touchmove', onTouchMove);
      document.addEventListener('touchend', onEnd);
    }

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [isDragging, handleMove]);

  return (
    <div className={`relative w-full py-4 select-none ${className}`}>
      {/* Track Background */}
      <div
        ref={trackRef}
        className="absolute top-1/2 left-0 w-full h-1.5 bg-gray-200 dark:bg-zinc-700 rounded-full -translate-y-1/2"
      >
        {/* Active Range */}
        <div
          className="absolute h-full rounded-full opacity-80"
          style={{
            left: `${getPercent(value.min)}%`,
            width: `${getPercent(value.max) - getPercent(value.min)}%`,
            backgroundColor: themeColor,
          }}
        />
      </div>

      {/* Min Thumb */}
      <div
        className="absolute top-1/2 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-md cursor-grab active:cursor-grabbing hover:scale-110 transition-transform -translate-y-1/2 focus:outline-none focus:ring-2 focus:ring-offset-2 z-10"
        style={{
          left: `${getPercent(value.min)}%`,
          transform: 'translate(-50%, -50%)',
          borderColor: isDragging === 'min' ? themeColor : undefined,
        }}
        onMouseDown={() => setIsDragging('min')}
        onTouchStart={() => setIsDragging('min')}
      >
        {/* Tooltip on Hover/Drag */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[11px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          {value.min}
        </div>
      </div>

      {/* Max Thumb */}
      <div
        className="absolute top-1/2 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-md cursor-grab active:cursor-grabbing hover:scale-110 transition-transform -translate-y-1/2 focus:outline-none focus:ring-2 focus:ring-offset-2 z-10"
        style={{
          left: `${getPercent(value.max)}%`,
          transform: 'translate(-50%, -50%)',
          borderColor: isDragging === 'max' ? themeColor : undefined,
        }}
        onMouseDown={() => setIsDragging('max')}
        onTouchStart={() => setIsDragging('max')}
      />
    </div>
  );
};
